/** §2.11 asset drag payload helpers. */
export const ASSET_DND_MIME = 'application/x-lumen-assets';

export type AssetDragPayload = { ids: string[]; source_board_id: string | null };

let dragImageCanvas: HTMLCanvasElement | null = null;

export function setAssetDragData(e: DragEvent | React.DragEvent, payload: AssetDragPayload, thumbUrl?: string | null, altKey = false): void {
  const dt = e.dataTransfer;
  if (!dt) return;
  dt.setData(ASSET_DND_MIME, JSON.stringify(payload));
  dt.setData('text/plain', payload.ids.join('\n'));
  dt.effectAllowed = 'copyMove';
  dt.dropEffect = altKey ? 'copy' : 'move';
  try {
    const c = (dragImageCanvas ??= document.createElement('canvas'));
    const size = 96;
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, size, size);
      ctx.fillStyle = '#222';
      roundRect(ctx, 0, 0, size, size, 8);
      ctx.fill();
      const img = thumbUrl ? findLoadedImage(thumbUrl) : null;
      if (img) {
        const s = Math.max(size / img.naturalWidth, size / img.naturalHeight);
        const w = img.naturalWidth * s;
        const h = img.naturalHeight * s;
        ctx.save();
        roundRect(ctx, 0, 0, size, size, 8);
        ctx.clip();
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        ctx.restore();
      }
      if (payload.ids.length > 1) {
        ctx.fillStyle = '#2563eb';
        ctx.beginPath();
        ctx.arc(size - 16, 16, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 13px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(payload.ids.length), size - 16, 16);
      }
      // the canvas must be in the DOM for some browsers
      if (!c.isConnected) {
        c.style.cssText = 'position:fixed;top:-200px;left:-200px;pointer-events:none';
        document.body.appendChild(c);
      }
      dt.setDragImage(c, 24, 24);
    }
  } catch {
    /* default drag image */
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function findLoadedImage(url: string): HTMLImageElement | null {
  const imgs = document.querySelectorAll<HTMLImageElement>('img');
  for (const i of imgs) if (i.currentSrc === url || i.src === url) return i.complete && i.naturalWidth > 0 ? i : null;
  return null;
}

export function hasAssetDrag(e: { dataTransfer: DataTransfer | null }): boolean {
  return !!e.dataTransfer && Array.from(e.dataTransfer.types).includes(ASSET_DND_MIME);
}

export function hasFileDrag(e: { dataTransfer: DataTransfer | null }): boolean {
  return !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');
}

export function readAssetDragData(e: { dataTransfer: DataTransfer | null }): AssetDragPayload | null {
  try {
    const raw = e.dataTransfer?.getData(ASSET_DND_MIME);
    if (!raw) return null;
    const p = JSON.parse(raw) as AssetDragPayload;
    return Array.isArray(p.ids) ? p : null;
  } catch {
    return null;
  }
}
